// CallScreen.jsx (FULL) — folded = NO overlay/background, sticks to TOP during routing
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { sipClient as sipTr } from "../../translate";
import PhoneIcon from "@mui/icons-material/Phone";
import CallEndIcon from "@mui/icons-material/CallEnd";
import CallIcon from "@mui/icons-material/Call";
import DialpadIcon from "@mui/icons-material/Dialpad";
import MicOffIcon from "@mui/icons-material/MicOff";
import MicIcon from "@mui/icons-material/Mic";
import PauseIcon from "@mui/icons-material/Pause";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import CallSplitIcon from "@mui/icons-material/CallSplit";
import HistoryIcon from "@mui/icons-material/History";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";

export default function CallScreen({
  open,
  onClose,

  callState,
  number,
  seconds,

  onAccept,
  onReject,
  onHangup,

  onSendDtmf,

  muted = false,
  held = false,
  onToggleMute,
  onToggleHold,

  onTransfer,
  canTransfer = false,

  displayName,
}) {
  const langIndex = +localStorage.getItem("atsLang") || 1;
  const isIncoming = callState === "Incoming";
  const isCalling = callState === "Calling";
  const isEstablished = callState === "Established";

  const [folded, setFolded] = useState(false);
  const [tab, setTab] = useState("actions");
  const [panelMode, setPanelMode] = useState(null); // null | 'transfer' | 'keypad'
  const [transferTo, setTransferTo] = useState("");
  const [dragPos, setDragPos] = useState({ x: 0, y: 0 });
  const dragRef = useRef({ isDragging: false, startX: 0, startY: 0, startPosX: 0, startPosY: 0 });
  const dragPosRef = useRef(dragPos);
  dragPosRef.current = dragPos;

  const dialPad = ["1","2","3","4","5","6","7","8","9","*","0","#"];
  const transferToRef = useRef("");

  const sendDtmfChar = useCallback((char) => {
    if (char && char !== "+" && onSendDtmf) onSendDtmf(char);
  }, [onSendDtmf]);

  const handleKeypadPress = (d, isTransferPanel) => {
    if (isTransferPanel) {
      setTransferTo((prev) => {
        const wasEmpty = !prev;
        const next = prev + d;
        if (onSendDtmf) {
          if (wasEmpty && d !== "#") {
            sendDtmfChar("#");
            setTimeout(() => sendDtmfChar(d), 50);
          } else {
            sendDtmfChar(d);
          }
        }
        return next;
      });
    } else {
      if (onSendDtmf) sendDtmfChar(d);
    }
  };

  const handleTransferInputChange = (e) => {
    const raw = e.target.value.replace(/[^\d*#+]/g, "");
    const prev = transferToRef.current;
    setTransferTo(raw);
    transferToRef.current = raw;

    if (!onSendDtmf) return;
    if (raw.length > prev.length) {
      const added = raw.slice(prev.length);
      if (prev.length === 0 && added.length > 0) {
        sendDtmfChar("#");
        setTimeout(() => {
          added.split("").forEach((c, i) => {
            setTimeout(() => sendDtmfChar(c), (i + 1) * 80);
          });
        }, 50);
      } else {
        added.split("").forEach((c, i) => {
          setTimeout(() => sendDtmfChar(c), i * 80);
        });
      }
    }
  };

  useEffect(() => {
    transferToRef.current = transferTo;
  }, [transferTo]);

  useEffect(() => {
    if (callState === "Terminated" || callState === "idle" || !open) {
      setTransferTo("");
      transferToRef.current = "";
      setPanelMode(null);
    }
  }, [callState, open]);

  const [isDragging, setIsDragging] = useState(false);

  const handleDragStart = useCallback((clientX, clientY) => {
    const { x, y } = dragPosRef.current;
    dragRef.current = {
      isDragging: true,
      startX: clientX,
      startY: clientY,
      startPosX: x,
      startPosY: y,
    };
    setIsDragging(true);
  }, []);

  const handleDragMove = useCallback((e) => {
    if (!dragRef.current.isDragging) return;
    if (e.touches) e.preventDefault();
    const { startX, startY, startPosX, startPosY } = dragRef.current;
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    setDragPos({
      x: startPosX + (clientX - startX),
      y: startPosY + (clientY - startY),
    });
  }, []);

  const handleDragEnd = useCallback(() => {
    dragRef.current.isDragging = false;
    setIsDragging(false);
  }, []);

  useEffect(() => {
    if (!folded) return;
    const onMove = (e) => handleDragMove(e);
    const onEnd = () => handleDragEnd();
    document.addEventListener("mousemove", onMove);
    document.addEventListener("mouseup", onEnd);
    document.addEventListener("touchmove", onMove, { passive: false });
    document.addEventListener("touchend", onEnd);
    return () => {
      document.removeEventListener("mousemove", onMove);
      document.removeEventListener("mouseup", onEnd);
      document.removeEventListener("touchmove", onMove);
      document.removeEventListener("touchend", onEnd);
    };
  }, [folded, handleDragMove, handleDragEnd]);

  const title = useMemo(() => {
    if (isIncoming) return "Incoming call";
    if (isCalling) return "Calling…";
    if (isEstablished) return "In call";
    return callState && callState !== "idle" ? callState : "Call";
  }, [callState, isIncoming, isCalling, isEstablished]);

  const time = useMemo(() => {
    const sec = Number(seconds || 0);
    const mm = String(Math.floor(sec / 60)).padStart(2, "0");
    const ss = String(sec % 60).padStart(2, "0");
    return `${mm}:${ss}`;
  }, [seconds]);

  const GREEN = "#16a34a";
  const GREEN_DARK = "#0f7a33";
  const BORDER = "rgba(22,163,74,0.25)";

  const pill = {
    display: "inline-flex",
    alignItems: "center",
    gap: 8,
    padding: "6px 10px",
    borderRadius: 999,
    border: `1px solid ${BORDER}`,
    background: "#ffffff",
    fontSize: 12,
    fontWeight: 900,
    color: "#0f172a",
  };

  const topBtn = {
    width: 34,
    height: 34,
    borderRadius: 12,
    border: "1px solid rgba(148,163,184,0.35)",
    background: "#ffffff",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  };

  const iconBtn = (active = false) => ({
    width: 56,
    height: 56,
    borderRadius: 16,
    margin: "auto",
    border: `1px solid ${
      active ? "rgba(22,163,74,0.45)" : "rgba(148,163,184,0.35)"
    }`,
    background: "#ffffff",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    gap: 6,
    cursor: "pointer",
    userSelect: "none",
    boxShadow: active
      ? "0 10px 24px rgba(22,163,74,0.18)"
      : "0 8px 18px rgba(15,23,42,0.08)",
  });

  const iconLabel = {
    fontSize: 10.5,
    fontWeight: 900,
    color: "#334155",
    lineHeight: 1,
  };

  const actionTabBtn = (active) => ({
    flex: 1,
    border: "none",
    cursor: "pointer",
    borderRadius: 12,
    padding: "8px 10px",
    fontWeight: 900,
    fontSize: 13,
    color: active ? "#0f172a" : "#64748b",
    background: active ? "rgba(22,163,74,0.14)" : "transparent",
  });

  const bigHangup = {
    width: 52,
    height: 52,
    borderRadius: "50%",
    border: "none",
    background: "#ef4444",
    color: "#fff",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    boxShadow: "0 16px 36px rgba(239,68,68,0.35)",
  };

  if (!open) return null;

  // ✅ FOLDED: draggable fixed top bar (never when incoming – always full screen)
  if (folded && !isIncoming) {
    return (
      <div
        onMouseDown={(e) => {
          if (e.button === 0 && !e.target.closest("button")) {
            handleDragStart(e.clientX, e.clientY);
          }
        }}
        onTouchStart={(e) => {
          if (!e.target.closest("button")) {
            handleDragStart(e.touches[0].clientX, e.touches[0].clientY);
          }
        }}
        style={{
          position: "fixed",
          top: 12,
          left: "50%",
          transform: `translate(calc(-50% + ${dragPos.x}px), ${dragPos.y}px)`,
          width: "min(520px, calc(100% - 24px))",
          zIndex: 3000,
          borderRadius: 18,
          border: `1px solid ${BORDER}`,
          background: "#ffffff",
          boxShadow: "0 18px 50px rgba(0,0,0,0.22)",
          padding: 12,
          display: "flex",
          alignItems: "center",
          gap: 10,
          pointerEvents: "auto",
          cursor: isDragging ? "grabbing" : "grab",
          userSelect: "none",
        }}
      >
        <div
          style={{
            width: 40,
            height: 40,
            borderRadius: "50%",
            background: "rgba(22,163,74,0.12)",
            border: `1px solid ${BORDER}`,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <PhoneIcon style={{ color: GREEN }} />
        </div>

        <div style={{ flex: 1, minWidth: 0 }}>
          <div
            style={{
              fontWeight: 900,
              color: "#0f172a",
              fontSize: 14,
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {title} · {displayName || "Caller"}
          </div>
          <div style={{ fontSize: 12, color: "#64748b", marginTop: 2 }}>
            <b style={{ color: "#334155" }}>{number || "—"}</b>
            {isEstablished ? ` · ${time}` : ""}
          </div>
        </div>

        <button
          type="button"
          onClick={() => setFolded(false)}
          style={topBtn}
          title="Unfold"
        >
          <ExpandMoreIcon style={{ color: "#334155" }} />
        </button>

        <button
          type="button"
          onClick={onHangup}
          style={{
            ...topBtn,
            background: "rgba(239,68,68,0.12)",
            border: "1px solid rgba(239,68,68,0.25)",
          }}
          title="Hangup"
        >
          <CallEndIcon style={{ color: "#ef4444" }} />
        </button>
      </div>
    );
  }

  // FULL overlay mode
  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 2999,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 14,
        background: "rgba(2, 6, 23, 0.45)",
      }}
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: "min(360px, 100%)",
          maxHeight: "calc(100vh - 28px)",
          borderRadius: 20,
          overflow: "auto",
          border: `1px solid ${BORDER}`,
          boxShadow: "0 22px 60px rgba(0,0,0,0.35)",
          background: "#ffffff",
        }}
      >
        {/* Top bar */}
        <div
          style={{
            padding: 12,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 10,
            borderBottom: "1px solid rgba(148,163,184,0.25)",
          }}
        >
          <div style={pill}>
            <PhoneIcon style={{ color: GREEN }} />
            {title}
          </div>

          {!isIncoming && (
            <button
              type="button"
              onClick={() => setFolded(true)}
              style={topBtn}
              title="Fold to top"
            >
              <ExpandLessIcon style={{ color: "#334155" }} />
            </button>
          )}
        </div>

        {/* Caller header */}
        <div style={{ padding: "12px 12px 10px 12px" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <div
              style={{
                width: 50,
                height: 50,
                borderRadius: "50%",
                background: "rgba(22,163,74,0.12)",
                border: `1px solid ${BORDER}`,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <PhoneIcon style={{ color: GREEN_DARK }} />
            </div>

            <div style={{ flex: 1, minWidth: 0 }}>
              <div
                style={{
                  fontSize: 18,
                  fontWeight: 900,
                  color: "#0f172a",
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              >
                {displayName || "Caller"}
              </div>

              <div style={{ marginTop: 4, display: "flex", gap: 8, flexWrap: "wrap" }}>
                <span style={{ ...pill, padding: "6px 9px" }}>{number || "—"}</span>
                <span style={{ ...pill, padding: "6px 9px" }}>
                  {isEstablished ? time : "00:00"}
                </span>
              </div>
            </div>
          </div>

          {/* Tabs */}
          <div
            style={{
              marginTop: 10,
              background: "#ffffff",
              border: "1px solid rgba(148,163,184,0.25)",
              borderRadius: 16,
              padding: 6,
              display: "flex",
              gap: 6,
            }}
          >
            <button
              type="button"
              onClick={() => setTab("actions")}
              style={actionTabBtn(tab === "actions")}
            >
              Actions
            </button>
            <button
              type="button"
              onClick={() => setTab("history")}
              style={{
                ...actionTabBtn(tab === "history"),
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 8,
              }}
            >
              <HistoryIcon style={{ fontSize: 18 }} />
              History
            </button>
          </div>
        </div>

        {/* Content */}
        <div style={{ padding: "0 12px 12px 12px" }}>
          {tab === "history" ? (
            <div
              style={{
                borderRadius: 18,
                border: "1px solid rgba(148,163,184,0.25)",
                background: "#ffffff",
                padding: 12,
                color: "#64748b",
                fontSize: 13,
                minHeight: 100,
              }}
            >
              (Optional) Put call history here…
            </div>
          ) : (
            <>
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(4, 1fr)",
                  gap: 10,
                  marginBottom: 10,
                }}
              >
                <button
                  type="button"
                  disabled={!isEstablished || !canTransfer}
                  onClick={() => setPanelMode((m) => (m === "transfer" ? null : "transfer"))}
                  style={{
                    ...iconBtn(panelMode === "transfer"),
                    ...(!isEstablished && { opacity: 0.5, cursor: "not-allowed" }),
                  }}
                  title="Transfer"
                >
                  <CallSplitIcon style={{ color: isEstablished && canTransfer ? (panelMode === "transfer" ? GREEN : "#0f172a") : "#94a3b8" }} />
                  <div style={iconLabel}>transfer</div>
                </button>

                <button
                  type="button"
                  disabled={!isEstablished}
                  onClick={onToggleHold}
                  style={{
                    ...iconBtn(held),
                    ...(!isEstablished && { opacity: 0.5, cursor: "not-allowed" }),
                  }}
                  title={held ? "Resume" : "Hold"}
                >
                  {held ? (
                    <PlayArrowIcon style={{ color: GREEN }} />
                  ) : (
                    <PauseIcon style={{ color: isEstablished ? "#0f172a" : "#94a3b8" }} />
                  )}
                  <div style={iconLabel}>hold</div>
                </button>

                <button
                  type="button"
                  disabled={!isEstablished}
                  onClick={() => setPanelMode((m) => (m === "keypad" ? null : "keypad"))}
                  style={{
                    ...iconBtn(panelMode === "keypad"),
                    ...(!isEstablished && { opacity: 0.5, cursor: "not-allowed" }),
                  }}
                  title="Keypad"
                >
                  <DialpadIcon style={{ color: isEstablished && panelMode === "keypad" ? GREEN : isEstablished ? "#0f172a" : "#94a3b8" }} />
                  <div style={iconLabel}>keypad</div>
                </button>

                <button
                  type="button"
                  disabled={!isEstablished}
                  onClick={onToggleMute}
                  style={{
                    ...iconBtn(muted),
                    ...(!isEstablished && { opacity: 0.5, cursor: "not-allowed" }),
                  }}
                  title={muted ? "Unmute" : "Mute"}
                >
                  {muted ? (
                    <MicOffIcon style={{ color: GREEN }} />
                  ) : (
                    <MicIcon style={{ color: isEstablished ? "#0f172a" : "#94a3b8" }} />
                  )}
                  <div style={iconLabel}>mute</div>
                </button>
              </div>

              {isEstablished && panelMode === "transfer" && (
                <div
                  style={{
                    borderRadius: 18,
                    border: "1px solid rgba(148,163,184,0.25)",
                    background: "#ffffff",
                    padding: 10,
                    marginBottom: 10,
                  }}
                >
                  <div style={{ fontSize: 13, fontWeight: 900, color: "#0f172a", marginBottom: 8 }}>
                    Transfer
                  </div>
                  <div style={{ marginBottom: 10 }}>
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        height: 42,
                        padding: "0 12px",
                        borderRadius: 14,
                        border: "2px solid rgba(148,163,184,0.35)",
                        background: "#fff",
                      }}
                    >
                      <input
                        value={transferTo}
                        onChange={handleTransferInputChange}
                        placeholder={sipTr.number?.[langIndex] ?? "number"}
                        style={{
                          flex: 1,
                          border: "none",
                          outline: "none",
                          fontWeight: 700,
                          background: "transparent",
                        }}
                      />
                    </div>
                  </div>
                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: "repeat(3, 1fr)",
                      gap: 9,
                    }}
                  >
                    {dialPad.map((d) => (
                      <button
                        key={d}
                        type="button"
                        onClick={() => handleKeypadPress(d, true)}
                        style={{
                          height: 50,
                          borderRadius: 16,
                          border: "1px solid rgba(148,163,184,0.35)",
                          background: "#ffffff",
                          cursor: "pointer",
                          fontSize: 18,
                          fontWeight: 900,
                          color: "#0f172a",
                        }}
                      >
                        {d}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {isEstablished && panelMode === "keypad" && (
                <div
                  style={{
                    borderRadius: 18,
                    border: "1px solid rgba(148,163,184,0.25)",
                    background: "#ffffff",
                    padding: 10,
                    marginBottom: 10,
                  }}
                >
                  <div style={{ fontSize: 13, fontWeight: 900, color: "#0f172a", marginBottom: 8 }}>
                    Keypad
                  </div>
                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: "repeat(3, 1fr)",
                      gap: 9,
                    }}
                  >
                    {dialPad.map((d) => (
                      <button
                        key={d}
                        type="button"
                        onClick={() => handleKeypadPress(d, false)}
                        style={{
                          height: 50,
                          borderRadius: 16,
                          border: "1px solid rgba(148,163,184,0.35)",
                          background: "#ffffff",
                          cursor: "pointer",
                          fontSize: 18,
                          fontWeight: 900,
                          color: "#0f172a",
                        }}
                      >
                        {d}
                      </button>
                    ))}
                  </div>
                </div>
              )}

            </>
          )}
        </div>

        {/* Bottom */}
        <div
          style={{
            padding: "12px 12px 14px 12px",
            borderTop: "1px solid rgba(148,163,184,0.25)",
            background: "#ffffff",
          }}
        >
          {isIncoming ? (
            <div style={{ display: "flex", gap: 75, justifyContent: "center" }}>
              <button
                onClick={onAccept}
                title="Accept"
                style={{
                  width: 52,
                  height: 52,
                  borderRadius: "50%",
                  border: "none",
                  cursor: "pointer",
                  background: GREEN,
                  color: "#fff",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  boxShadow: "0 12px 28px rgba(22,163,74,0.35)",
                }}
              >
                <CallIcon style={{ fontSize: 26 }} />
              </button>
              <button
                onClick={onReject}
                title="Decline"
                style={{
                  width: 52,
                  height: 52,
                  borderRadius: "50%",
                  border: "1px solid rgba(239,68,68,0.35)",
                  cursor: "pointer",
                  background: "rgba(239,68,68,0.10)",
                  color: "#ef4444",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  boxShadow: "0 12px 28px rgba(239,68,68,0.2)",
                }}
              >
                <CallEndIcon style={{ fontSize: 26 }} />
              </button>
            </div>
          ) : (
            <div style={{ display: "flex", justifyContent: "center" }}>
              <button onClick={onHangup} style={bigHangup} title="End call">
                <CallEndIcon style={{ fontSize: 26 }} />
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}